import React, { ReactElement } from 'react';
import { TableRowProps } from './table-row.types';
import TableCell from '../TableCell/table-cell';
import { TableCellProps } from '../TableCell/table-cell.types';
import { CellTypes, HeadProps, GroupHeadProps } from '../table.types';
import { SortOrderTypes } from '../../DataTable/dataTable.types';

// Builds the cells based on passed props like custom cells, if its a header, footer, etc. and more.
const getCells = (rowColumns: HeadProps[], props: TableRowProps): ReactElement[] => {
  const {
    type,
    rowIndex,
    data,
    customCells,
    highlightedCells,
    highlightHead,
    dark,
    firstRow,
    lastRow,
    headerHandlers,
    columnsState,
    columnSortIndex,
    highlightSortedColumn,
    highlightColor,
    isLoading,
    replaceCustomCellWithSkeleton,
  } = props;
  const columns: ReactElement[] = [];
  let foundFirstGroupStart: boolean = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rowColumns.forEach((column: any, i: number) => {
    const columnState: { sortable?: boolean; sortState?: SortOrderTypes } =
      (columnsState && columnsState[`col-${i}`]) || {};

    const {
      content,
      dataField,
      fixedWidth,
      pinned,
      colgroupStart,
      colgroupEnd,
      placeholder,
      alignment,
      colgroupSpan,
      highlighted,
    } = column;

    const { sortable, sortState } = columnState;

    const cellProps: TableCellProps = {
      cellType: type || CellTypes.BODY,
      content: type === CellTypes.SUPER_HEADER || type === CellTypes.HEADER ? content : data?.[dataField],
      handlers: type === CellTypes.HEADER ? headerHandlers : undefined,
      rowIndex: typeof rowIndex !== 'undefined' ? rowIndex : 0,
      columnIndex: i,
      fixedWidth,
      firstColPinned: i === 0 && pinned,
      lastColPinned: i === rowColumns.length - 1 && pinned,
      colgroupEnd,
      colgroupStart,
      placeholder,
      colgroupSpan,
      tableIndex: props.tableIndex,
      alignment,
      lastColumn: i === rowColumns.length - 1,
      columnHighlighted: highlighted,
      highlightHead,
      firstRow,
      lastRow,
      dark,
      columnSortIndex,
      sortState,
      sortable: type === CellTypes.SUPER_HEADER ? false : sortable,
      highlightSortedColumn,
      highlightColor,
      isLoading,
      replaceCustomCellWithSkeleton,
    };

    if (highlightedCells) {
      for (let c: number = 0; c < highlightedCells.length; c++) {
        if (highlightedCells[c].dataField === dataField && highlightedCells[c].rowIndex === rowIndex) {
          cellProps.cellHighlighted = true;
        }
      }
    }

    // Discover the start of the first column group for cell border generating purposes
    if (!foundFirstGroupStart && colgroupStart) {
      cellProps.firstGroupStart = true;
      foundFirstGroupStart = true;
    }

    // Create a key for each table cell in the row
    const key: string = `${type}-${rowIndex}-${i}`;

    // Find if a custom cell element exists
    let clonedElement: ReactElement | null = null;
    if (customCells) {
      customCells.forEach((custom: { cellType: string; dataField: string; customCell: React.ReactElement }) => {
        if (type === custom.cellType && dataField === custom.dataField) {
          clonedElement = React.cloneElement(custom.customCell, {
            ...props,
            ...cellProps,
          });
        }
      });
    }

    // Build the appropriate table cell element
    const tableCell: ReactElement = clonedElement ? (
      <TableCell {...cellProps} key={key} content={clonedElement} isCustom />
    ) : (
      <TableCell {...cellProps} key={key} />
    );

    columns.push(tableCell);
  });

  return columns;
};

const getColumns = (props: TableRowProps): ReactElement[] => {
  const { type, columnData } = props;

  let rowColumns: HeadProps[] = [];

  (columnData as []).forEach((column: HeadProps | GroupHeadProps) => {
    const columnAsGroup: GroupHeadProps = column as GroupHeadProps;

    // If we're dealing with a group of columns
    if (Object.prototype.hasOwnProperty.call(columnAsGroup, 'groupColumns')) {
      // Retrieve the passed columns
      const columns: HeadProps[] = columnAsGroup.groupColumns;

      // If we're looking at super headers,
      // create the special group spans
      if (type === CellTypes.SUPER_HEADER) {
        const rowCol: HeadProps = {
          content: columnAsGroup.groupTitle,
          colgroupSpan: columnAsGroup.groupColumns.length,
        };

        rowColumns.push(rowCol);

        // If we're dealing with run-of-the-mill columns
        // Set appropriate colgroup starts and ends
        // and signify that it belongs to a superheader group
      } else {
        const hasSuperHeader: boolean = !!columnAsGroup.groupTitle;
        columns[columns.length - 1].colgroupEnd = true;
        columns[0].colgroupStart = true;

        const columns2: HeadProps[] = columns.map((entry: HeadProps) => {
          entry.hasSuperHeader = hasSuperHeader;
          return entry;
        });

        rowColumns = rowColumns.concat(columns2);
      }
    } else {
      const columnAsSingle: HeadProps = column as HeadProps;
      // if the type isn't super header, pass it along
      // else, pass a blank placeholder super-header row
      if (type !== CellTypes.SUPER_HEADER) {
        rowColumns.push(columnAsSingle);
      } else {
        rowColumns.push({
          content: <br />,
          colgroupSpan: 1,
          pinned: columnAsSingle.pinned,
        });
      }
    }
  });

  return getCells(rowColumns, props);
};

const TableRow = (props: TableRowProps) => {
  const { rowIndex, tableRowHandlers, selectedRowIndex, underlinedRowIndex } = props;

  // Build the row
  const tableRow: ReactElement[] = getColumns(props);
  const selected = selectedRowIndex === rowIndex;
  const underlined = !!(typeof underlinedRowIndex !== 'undefined' && underlinedRowIndex === rowIndex);
  return (
    <tr key={`row-${rowIndex}`} {...tableRowHandlers} data-selected={selected} data-underlined={underlined}>
      {tableRow}
    </tr>
  );
};

export default TableRow;
